import { useState, useEffect } from 'react';
import { Company, CompanyCreateRequest, Address, Coordinates, LocationContactInfo, Location } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface CompanyFormProps {
  company?: Company;
  onSubmit: (data: CompanyCreateRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
}

export default function CompanyForm({ company, onSubmit, onCancel, loading, isEdit }: CompanyFormProps) {
  const [formData, setFormData] = useState<CompanyCreateRequest>({
    name: '',
    description: '',
    logo: '',
    website: '',
    locations: []
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fetchingLocation, setFetchingLocation] = useState<number | null>(null);

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        description: company.description || '',
        logo: company.logo || '',
        website: company.website || '',
        locations: company.locations || []
      });
    }
  }, [company]);

  const handleInputChange = (field: keyof CompanyCreateRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const addLocation = () => {
    const newLocation: Location = {
      name: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      coordinates: {
        latitude: 0,
        longitude: 0
      },
      isMainOffice: formData.locations?.length === 0,
      contactInfo: {
        phone: '',
        email: ''
      },
      isActive: true
    };

    setFormData(prev => ({
      ...prev,
      locations: [...(prev.locations || []), newLocation]
    }));
  };

  const removeLocation = (index: number) => {
    setFormData(prev => {
      const newLocations = [...(prev.locations || [])];
      const removedLocation = newLocations[index];
      newLocations.splice(index, 1);

      if (removedLocation.isMainOffice && newLocations.length > 0) {
        newLocations[0].isMainOffice = true;
      }

      return {
        ...prev,
        locations: newLocations
      };
    });
  };

  const updateLocation = (index: number, field: string, value: any) => {
    setFormData(prev => {
      const newLocations = [...(prev.locations || [])];
      const location = { ...newLocations[index] };
      
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        if (parent === 'address') {
          location.address = {
            ...location.address,
            [child]: value
          } as Address;
        } else if (parent === 'coordinates') {
          location.coordinates = {
            ...location.coordinates,
            [child]: value
          } as Coordinates;
        } else if (parent === 'contactInfo') {
          location.contactInfo = {
            ...location.contactInfo,
            [child]: value
          } as LocationContactInfo;
        }
      } else if (field === 'isMainOffice') {
        if (value) {
          newLocations.forEach((loc, idx) => {
            loc.isMainOffice = idx === index;
          });
        }
        location.isMainOffice = value;
      } else {
        (location as any)[field] = value;
      }

      newLocations[index] = location;
      return {
        ...prev,
        locations: newLocations
      };
    });
  };

  const fetchCurrentLocation = async (index: number) => {
    setFetchingLocation(index);
    
    try {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          updateLocation(index, 'coordinates.latitude', position.coords.latitude);
          updateLocation(index, 'coordinates.longitude', position.coords.longitude);
          setFetchingLocation(null);
        },
        (error) => {
          console.error('Error getting location:', error);
          setFetchingLocation(null);
        }
      );
    } catch (error) {
      console.error('Failed to get location:', error);
      setFetchingLocation(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Company name is required';
    }

    if (!formData.locations || formData.locations.length === 0) {
      newErrors.locations = 'At least one location is required';
    } else {
      const mainOfficeCount = formData.locations.filter(loc => loc.isMainOffice).length;
      if (mainOfficeCount === 0) {
        newErrors.locations = 'One location must be marked as main office';
      } else if (mainOfficeCount > 1) {
        newErrors.locations = 'Only one location can be marked as main office';
      }

      formData.locations.forEach((location, index) => {
        if (!location.name.trim()) {
          newErrors['location_' + index + '_name'] = 'Location name is required';
        }
        if (!location.address.street.trim()) {
          newErrors['location_' + index + '_street'] = 'Street address is required';
        }
        if (!location.address.city.trim()) {
          newErrors['location_' + index + '_city'] = 'City is required';
        }
        if (!location.address.state.trim()) {
          newErrors['location_' + index + '_state'] = 'State is required';
        }
        if (!location.address.zipCode.trim()) {
          newErrors['location_' + index + '_zipCode'] = 'Zip code is required';
        }
        if (!location.address.country.trim()) {
          newErrors['location_' + index + '_country'] = 'Country is required';
        }
        
        if (location.coordinates.latitude < -90 || location.coordinates.latitude > 90) {
          newErrors['location_' + index + '_latitude'] = 'Latitude must be between -90 and 90';
        }
        if (location.coordinates.longitude < -180 || location.coordinates.longitude > 180) {
          newErrors['location_' + index + '_longitude'] = 'Longitude must be between -180 and 180';
        }
      });
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Company Details' : 'Set Up Your Company'}
          </h2>
          <p className="text-gray-600 mt-2">
            Configure your company information and locations
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Company Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter company name"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <Input
                  type="url"
                  value={formData.website || ''}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="https://company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo URL
                </label>
                <Input
                  type="url"
                  value={formData.logo || ''}
                  onChange={(e) => handleInputChange('logo', e.target.value)}
                  placeholder="https://company.com/logo.png"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Brief company description..."
              />
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between border-b pb-2">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Company Locations</h3>
                <p className="text-sm text-gray-500 mt-1">Add all your office locations</p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={addLocation}
                size="sm"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Location
              </Button>
            </div>
            
            {errors.locations && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-red-600 text-sm">{errors.locations}</p>
              </div>
            )}

            {formData.locations && formData.locations.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No locations</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by adding your first office location.</p>
                <div className="mt-6">
                  <Button
                    type="button"
                    onClick={addLocation}
                    size="sm"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add First Location
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {formData.locations?.map((location, index) => (
                <Card 
                  key={index} 
                  className={(location.isMainOffice ? 'border-l-4 border-l-blue-500 bg-blue-50' : 'border-l-4 border-l-gray-300') + ' p-4'}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <h4 className="text-md font-medium text-gray-900">
                        Location {index + 1}
                      </h4>
                      {location.isMainOffice && (
                        <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">
                          Main Office
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={location.isMainOffice}
                          onChange={(e) => updateLocation(index, 'isMainOffice', e.target.checked)}
                          className="mr-2 rounded"
                        />
                        Set as Main Office
                      </label>
                      {formData.locations && formData.locations.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeLocation(index)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Location Name *
                      </label>
                      <Input
                        type="text"
                        value={location.name}
                        onChange={(e) => updateLocation(index, 'name', e.target.value)}
                        placeholder="e.g., Headquarters, Branch Office"
                        className={errors['location_' + index + '_name'] ? 'border-red-500' : ''}
                      />
                      {errors['location_' + index + '_name'] && (
                        <p className="text-red-500 text-xs mt-1">{errors['location_' + index + '_name']}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <Input
                        type="tel"
                        value={location.contactInfo?.phone || ''}
                        onChange={(e) => updateLocation(index, 'contactInfo.phone', e.target.value)}
                        placeholder="Office phone"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <Input
                        type="email"
                        value={location.contactInfo?.email || ''}
                        onChange={(e) => updateLocation(index, 'contactInfo.email', e.target.value)}
                        placeholder="Office email"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Street Address *
                      </label>
                      <Input
                        type="text"
                        value={location.address.street}
                        onChange={(e) => updateLocation(index, 'address.street', e.target.value)}
                        placeholder="Street address"
                        className={errors['location_' + index + '_street'] ? 'border-red-500' : ''}
                      />
                      {errors['location_' + index + '_street'] && (
                        <p className="text-red-500 text-xs mt-1">{errors['location_' + index + '_street']}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City *
                      </label>
                      <Input
                        type="text"
                        value={location.address.city}
                        onChange={(e) => updateLocation(index, 'address.city', e.target.value)}
                        placeholder="City"
                        className={errors['location_' + index + '_city'] ? 'border-red-500' : ''}
                      />
                      {errors['location_' + index + '_city'] && (
                        <p className="text-red-500 text-xs mt-1">{errors['location_' + index + '_city']}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State *
                      </label>
                      <Input
                        type="text"
                        value={location.address.state}
                        onChange={(e) => updateLocation(index, 'address.state', e.target.value)}
                        placeholder="State"
                        className={errors['location_' + index + '_state'] ? 'border-red-500' : ''}
                      />
                      {errors['location_' + index + '_state'] && (
                        <p className="text-red-500 text-xs mt-1">{errors['location_' + index + '_state']}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Zip Code *
                      </label>
                      <Input
                        type="text"
                        value={location.address.zipCode}
                        onChange={(e) => updateLocation(index, 'address.zipCode', e.target.value)}
                        placeholder="Zip code"
                        className={errors['location_' + index + '_zipCode'] ? 'border-red-500' : ''}
                      />
                      {errors['location_' + index + '_zipCode'] && (
                        <p className="text-red-500 text-xs mt-1">{errors['location_' + index + '_zipCode']}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country *
                      </label>
                      <Input
                        type="text"
                        value={location.address.country}
                        onChange={(e) => updateLocation(index, 'address.country', e.target.value)}
                        placeholder="Country"
                        className={errors['location_' + index + '_country'] ? 'border-red-500' : ''}
                      />
                      {errors['location_' + index + '_country'] && (
                        <p className="text-red-500 text-xs mt-1">{errors['location_' + index + '_country']}</p>
                      )}
                    </div>

                    <div className="col-span-2">
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Coordinates *
                        </label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => fetchCurrentLocation(index)}
                          disabled={fetchingLocation === index}
                          className="text-xs"
                        >
                          {fetchingLocation === index ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Getting Location...
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              Get Current Location
                            </>
                          )}
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Latitude *
                          </label>
                          <Input
                            type="number"
                            step="any"
                            value={location.coordinates.latitude}
                            onChange={(e) => updateLocation(index, 'coordinates.latitude', parseFloat(e.target.value) || 0)}
                            placeholder="0.000000"
                            className={errors['location_' + index + '_latitude'] ? 'border-red-500' : ''}
                          />
                          {errors['location_' + index + '_latitude'] && (
                            <p className="text-red-500 text-xs mt-1">{errors['location_' + index + '_latitude']}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Longitude *
                          </label>
                          <Input
                            type="number"
                            step="any"
                            value={location.coordinates.longitude}
                            onChange={(e) => updateLocation(index, 'coordinates.longitude', parseFloat(e.target.value) || 0)}
                            placeholder="0.000000"
                            className={errors['location_' + index + '_longitude'] ? 'border-red-500' : ''}
                          />
                          {errors['location_' + index + '_longitude'] && (
                            <p className="text-red-500 text-xs mt-1">{errors['location_' + index + '_longitude']}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-span-2">
                      <label className="flex items-center text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={location.isActive}
                          onChange={(e) => updateLocation(index, 'isActive', e.target.checked)}
                          className="mr-2 rounded"
                        />
                        Location is Active
                      </label>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : isEdit ? 'Update Company' : 'Create Company'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
